import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    useLazyGetPendingBingxQuery,
    useLazyGetPendingWithdrawalsQuery,
    useLazyGetPendingAlnyWithdrawalsQuery,
} from "../api";
import { useLazyAdminRussiaPendingTopUps } from "../api/wallet";
import { AppDispatch, RootState } from "../store";
import { resetCounts, updateCounts } from "../store/slices/pendingRequestsSlice";

const POLLING_INTERVAL = 30000;

export const usePendingRequestsWatcher = (): void => {
    const dispatch = useDispatch<AppDispatch>();
    const isAdmin = useSelector((state: RootState) =>
        state.auth.user?.roles?.includes("admin")
    );

    const [fetchWithdrawals] = useLazyGetPendingWithdrawalsQuery();
    const [fetchAlnyWithdrawals] = useLazyGetPendingAlnyWithdrawalsQuery();
    const [fetchBingx] = useLazyGetPendingBingxQuery();
    const [fetchRussiaTopups] = useLazyAdminRussiaPendingTopUps();

    useEffect(() => {
        if (!isAdmin) {
            dispatch(resetCounts());
            return;
        }

        let isMounted = true;

        const pollPendingRequests = async () => {
            try {
                const [
                    withdrawalsResult,
                    alnyResult,
                    bingxResult,
                    topupsResult,
                ] = await Promise.allSettled([
                    fetchWithdrawals().unwrap(),
                    fetchAlnyWithdrawals().unwrap(),
                    fetchBingx().unwrap(),
                    fetchRussiaTopups().unwrap(),
                ]);

                if (!isMounted) {
                    return;
                }

                let withdrawalsCount = 0;

                if (withdrawalsResult.status === "fulfilled") {
                    const fiatWithdrawals =
                        withdrawalsResult.value?.data?.withdrawals ?? [];
                    // Каждая запись = отдельная заявка; считаем только заявки в статусе pending
                    withdrawalsCount += fiatWithdrawals.filter(
                        (item: any) => item?.status === "pending"
                    ).length;
                }

                if (alnyResult.status === "fulfilled") {
                    withdrawalsCount += (alnyResult.value?.data ?? []).length;
                }

                const bingxCount =
                    bingxResult.status === "fulfilled"
                        ? (bingxResult.value?.data ?? []).length
                        : 0;

                const topupsCount =
                    topupsResult.status === "fulfilled"
                        ? (topupsResult.value?.data?.payments ?? []).length
                        : 0;

                dispatch(
                    updateCounts({
                        withdrawals: withdrawalsCount,
                        bingx: bingxCount,
                        topups: topupsCount,
                    })
                );
            } catch (error) {
                console.error("Failed to poll pending requests", error);
            }
        };

        pollPendingRequests();
        const intervalId = window.setInterval(
            pollPendingRequests,
            POLLING_INTERVAL
        );

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, [
        dispatch,
        fetchWithdrawals,
        fetchAlnyWithdrawals,
        fetchBingx,
        fetchRussiaTopups,
        isAdmin,
    ]);
};

